import { useState, useEffect } from "react";
import { FaCheck, FaTimes, FaMoneyBillWave } from "react-icons/fa";
import BASE_URL from "../../BASEURL";

export default function ReturnsRefunds() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReturns();
  }, []);

  const fetchReturns = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/returns`);
      const data = await res.json();
      if (data.success) {
        setReturns(data.returns || []);
      }
    } catch (error) {
      console.error("Error fetching returns:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- HELPERS ---------------- */
  const statusBadge = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-700";
      case "Approved":
        return "bg-green-100 text-green-700";
      case "Rejected":
        return "bg-red-100 text-red-700";
      case "Completed":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  /* ---------------- RETURN ACTIONS ---------------- */
  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${BASE_URL}/api/returns/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        fetchReturns();
      } else {
        alert(data.message || "Failed to update return request");
      }
    } catch (error) {
      console.error("Error updating return request:", error);
      alert("Error updating return request");
    }
  };

  if (loading) {
    return <div className="p-6">Loading returns & refunds...</div>;
  }

  // Filter refunds as approved return requests
  const refunds = returns.filter((r) => r.status === "Approved" || r.status === "Completed");

  return (
    <>
      {/* PAGE TITLE */}
      <h2 className="text-2xl font-heading font-bold mb-6 text-primary">
        Returns & Refunds Management
      </h2>

      {/* ================= RETURNS TABLE ================= */}
      <div className="bg-white rounded shadow mb-10 overflow-x-auto">
        <h3 className="text-lg font-semibold p-4 border-b text-primary">
          Return Requests
        </h3>

        <table className="w-full text-sm">
          <thead className="bg-primary/70 text-light">
            <tr>
              <th className="p-3 text-left">Order ID</th>
              <th className="p-3 text-left">User</th>
              <th className="p-3 text-left">Reason</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {returns.map((ret) => {
              const orderIdString = ret.order && ret.order._id 
                ? ret.order._id.substring(ret.order._id.length - 8).toUpperCase()
                : "N/A";
              return (
                <tr key={ret._id} className="border-t">
                  <td className="p-3 font-semibold">{orderIdString}</td>
                  <td className="p-3">{ret.user ? ret.user.name : "Guest User"}</td>
                  <td className="p-3">{ret.reason}</td>
                  <td className="p-3">
                    <span
                      className={`px-3 py-1 rounded text-xs font-semibold ${statusBadge(
                        ret.status
                      )}`}
                    >
                      {ret.status}
                    </span>
                  </td>
                  <td className="p-3 text-center space-x-3">
                    {ret.status === "Pending" && (
                      <>
                        <button
                          onClick={() => updateStatus(ret._id, "Approved")}
                          className="text-green-600 hover:text-green-800"
                          title="Approve"
                        >
                          <FaCheck className="inline" />
                        </button>
                        <button
                          onClick={() => updateStatus(ret._id, "Rejected")}
                          className="text-red-600 hover:text-red-800"
                          title="Reject"
                        >
                          <FaTimes className="inline" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {returns.length === 0 && (
              <tr>
                <td colSpan="5" className="p-4 text-center text-gray-500">
                  No return requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= REFUNDS TABLE ================= */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <h3 className="text-lg font-semibold p-4 border-b text-primary">
          Refund Requests (Approved Returns)
        </h3>

        <table className="w-full text-sm">
          <thead className="bg-primary/70 text-light">
            <tr>
              <th className="p-3 text-left">Order ID</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {refunds.map((ref) => {
              const orderIdString = ref.order && ref.order._id 
                ? ref.order._id.substring(ref.order._id.length - 8).toUpperCase()
                : "N/A";
              const amount = ref.order ? ref.order.totalPrice : 0;
              return (
                <tr key={ref._id} className="border-t">
                  <td className="p-3 font-semibold">{orderIdString}</td>
                  <td className="p-3">₹{amount}</td>
                  <td className="p-3">
                    <span
                      className={`px-3 py-1 rounded text-xs font-semibold ${statusBadge(
                        ref.status
                      )}`}
                    >
                      {ref.status === "Completed" ? "Refund Processed" : "Pending Refund"}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    {ref.status === "Approved" && (
                      <button
                        onClick={() => updateStatus(ref._id, "Completed")}
                        className="text-blue-600 hover:text-blue-800"
                        title="Process Refund"
                      >
                        <FaMoneyBillWave className="inline mr-1" /> Process Refund
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {refunds.length === 0 && (
              <tr>
                <td colSpan="4" className="p-4 text-center text-gray-500">
                  No pending or processed refunds.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
